import { Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { FiatDepositStepsData } from "@modules/+new/payments/features/fiat-deposit/domain/entities/fiat-deposit-steps-data.model";
import { PaymentOptionsFacade } from "@modules/+new/payments/features/payment-options/domain/+state/payment-options.facade";
import { FiatDepositRemote } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit.remote";
import { DryOfferRequestModel } from "@modules/+new/payments/features/fiat-deposit/domain/entities/dry-offer/dry-offer-request.model";
import { DryOffer } from "@modules/+new/payments/features/fiat-deposit/domain/entities/dry-offer/dry-offer.model";
import { WalletsFacade } from "@modules/+new/wallets/domain/+state/wallets.facade";
import { FiatWalletSummary } from "@modules/+new/wallets/domain/entities/fiat-wallet-summary.model";
import { PaymentOption } from "@modules/+new/payments/features/payment-options/domain/entities/payment-option.model";
import { Fiat } from "@modules/+new/assets/domain/entities/fiat.model";
import { map, switchMap } from "rxjs/operators";
import { FiatProviderDetails } from "@modules/+new/payments/features/payment-options/domain/entities/fiat-provider-details.model";

@Injectable({
	providedIn: "any"
})
export class FiatDepositDryOfferService {
	public constructor(
		private readonly paymentOptionsFacade: PaymentOptionsFacade,
		private readonly fiatDepositRemote: FiatDepositRemote,
		private readonly walletsFacade: WalletsFacade
	) {}

	public getDryOffer$(walletId: string, requestModel: DryOfferRequestModel): Observable<DryOffer> {
		return this.fiatDepositRemote.getDryOffer$(walletId, requestModel);
	}

	public getDryOfferByStepsData$(stepsData: FiatDepositStepsData): Observable<DryOffer> {
		const { walletGuid, paymentOptionId, depositAmount, includingFees } = stepsData;
		const fiat$ = this.getFiatWalletsSummaryByWalletGuid$(walletGuid).pipe(
			map((summary: FiatWalletSummary) => summary.fiat)
		);
		const provider$ = this.getPaymentOptionById$(paymentOptionId);

		return combineLatest([provider$, fiat$]).pipe(
			switchMap(([provider, fiat]) => {
				const fiatProviderDetails = provider?.fiatProviderDetails?.find(
					(details: FiatProviderDetails) => details?.fiatId === fiat?.id
				);
				const request = this.prepareGetDryOfferRequest(
					provider,
					fiat,
					fiatProviderDetails,
					depositAmount,
					includingFees
				);

				return this.getDryOffer$(walletGuid, request);
			})
		);
	}

	private prepareGetDryOfferRequest(
		provider: PaymentOption,
		fiat: Fiat,
		fiatProviderDetails: FiatProviderDetails,
		depositAmount: number,
		includingFees: boolean
	): DryOfferRequestModel {
		const feeDetails = fiatProviderDetails?.feeDetails;
		return {
			amount: depositAmount?.toString(),
			expected_fee_fix: feeDetails?.fix?.toString(),
			expected_fee_max: feeDetails?.max?.toString(),
			expected_fee_percent: feeDetails?.percent?.toString(),
			fiat_id: fiat.id?.toString(),
			is_fee_excl: !includingFees,
			payment_option_id: provider.id?.toString()
		};
	}

	private getFiatWalletsSummaryByWalletGuid$(fiatWalletGuid: string): Observable<FiatWalletSummary> {
		return this.walletsFacade.getFiatWalletsSummaryByWalletGuid$(fiatWalletGuid);
	}

	private getPaymentOptionById$(id: number): Observable<PaymentOption> {
		return this.paymentOptionsFacade.getPaymentOptionById$(id);
	}
}
